import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import axios from "axios";
import { useLocation, useNavigate } from "react-router";
import { route_base_url } from "../../pages/stops/utils";
import Update_Icon from "../../images/table/Update_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DeleteModal from "../common/DeleteModal";
import SubTitle from "../common/SubTitle";
import TitleBar from "../navbar/TitleBar";
import NavMenu from "../navbar/NavMenu";
import SearchFilter from "../common/SearchFilter";
import DynamicTable from "../common/DynamicTable";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import config from "../../config";
import { useTranslation } from "react-i18next";
import Footer from "../common/Footer";

function RouteSchedule() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;
  console.log("RouteSchedule: id from location state", id);
  const [scheduleData, setScheduleData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  const headers = [
    { key: "Schedules", value: "Schedules" },
    { key: "Description", value: "Description" },
    { key: "Start Time", value: "Start Time" },
    { key: "End Time", value: "End Time" },
    { key: "Repetition", value: "Repetition" },
    { key: ACTIONS_COLUMN_ID, value: "Actions" },
  ];

  const customActions = [
    { id: "update", label: "Update", icon: Update_Icon },
    { id: "delete", label: "Delete", icon: Delete_Icon, className: "delete" },
  ];

  const headerObjects = [
    { id: "name", label: "Schedules", sortable: true },
    { id: "description", label: "Description", sortable: false },
    { id: "startTime", label: "Start Time", sortable: true },
    { id: "endTime", label: "End Time", sortable: false },
    { id: "repitation", label: "Repetition", sortable: false },
  ];

  const filterConfig = [
    {
      id: "repitation",
      name: "repitation",
      label: "Repetition",
      options: [
        // { value: 'All', label: 'All' },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "onetime", label: "One time" },
      ],
    },
  ];

  useEffect(() => {
    async function fetchSchedules() {
      try {
        if (id?.routeId) {
          setIsLoading(true);
          const response = await axios.get(route_base_url, {
            params: { routeId: id.id },
            withCredentials: true,
          });
          console.log(
            "route for schedules",
            JSON.stringify(response.data, null, 2),
          );
          setScheduleData(response.data);

          if (response.data && Array.isArray(response.data)) {
            setTableData(formatScheduleData(response.data));
          }

          setIsLoading(false);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.log("error in fetching route", error);
        setIsLoading(false);
      }
    }
    fetchSchedules();
  }, [id, navigate]);

  const formatScheduleData = (schedules) => {
    return schedules.map((schedule) => {
      const startTime = schedule.startTime
        ? new Date(schedule.startTime).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "N/A";

      const endTime = schedule.endTime
        ? new Date(schedule.endTime).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "N/A";

      return {
        Schedules: schedule.name || "N/A",
        Description: schedule.description || "N/A",
        "Start Time": startTime,
        "End Time": endTime,
        Repetition: schedule.repitation
          ? schedule.repitation.charAt(0).toUpperCase() +
            schedule.repitation.slice(1).toLowerCase()
          : "N/A",
        originalData: schedule,
      };
    });
  };

  const handleSearchChange = (searchText) => {
    if (!Array.isArray(scheduleData)) return;

    if (!searchText) {
      setTableData(formatScheduleData(scheduleData));
      return;
    }

    const filtered = scheduleData.filter(
      (item) =>
        item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchText.toLowerCase()),
    );

    setTableData(formatScheduleData(filtered));
  };

  const handleFilterChange = (filterValues) => {
    console.log("Filter values:", filterValues);
    if (!Array.isArray(scheduleData)) {
      console.log("scheduleData is not an array:", scheduleData);
      return;
    }

    const value = filterValues.repitation;
    console.log("Selected repitation:", value);

    // Handle "All" or empty/undefined values
    if (value === "All" || !value || value === "") {
      setTableData(formatScheduleData(scheduleData));
      return;
    }

    // Filter with case-insensitive comparison
    const filtered = scheduleData.filter((item) => {
      const itemRepitation = item.repitation?.toLowerCase();
      const selectedValue = value.toLowerCase();
      return itemRepitation === selectedValue;
    });

    console.log("Filtered data:", filtered);
    setTableData(formatScheduleData(filtered));
  };

  const handleActionClick = (actionId, rowIndex) => {
    const item = tableData[rowIndex]?.originalData;
    if (!item) {
      console.error("Item not found at rowIndex:", rowIndex);
      return;
    }

    switch (actionId) {
      case "update":
        navigate("/schedule/add", {
          state: {
            id: {
              routeId: id.routeId,
              name: id.name,
              scheduleId: item.id,
            },
          },
        });
        break;
      case "delete":
        setScheduleToDelete(item);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!scheduleToDelete) return;

    try {
      await axios.delete(`${config.baseURL}/schedule`, {
        data: { scheduleId: scheduleToDelete.id },
        withCredentials: true,
      });
      setTableData((prev) =>
        prev.filter((row) => row.originalData.id !== scheduleToDelete.id),
      );
      setScheduleData((prev) =>
        prev.filter((row) => row.id !== scheduleToDelete.id),
      );
      setIsDeleteModalOpen(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error("Error deleting schedule:", error);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setScheduleToDelete(null);
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <>
      <TitleBar />
      <NavMenu />
      <SubTitle
        title="Route Schedules"
        path={`${orgName} > Routes > ${id?.name} > Route Schedules`}
        breadcrumbHandlers={{
          [id?.name]: {
            to: "/route/add",
            state: { rowData: id }, 
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          height: "60px",
          justifyContent: "space-between",
          padding: "12px 16px",
          margin: "0px 40px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 2px 5px 0px #00000040",
        }}
      >
        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            letterSpacing: "0%",
            display: "feet",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            {t("Route")}:
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {id?.name || "-"}
          </span>
        </Box>
        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            {t("Route ID")}:
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {id?.routeId || "-"}
          </span>
        </Box>
      </Box>
      <Box
        sx={{
          margin: "20px 40px",
          borderRadius: "10px",
          padding: "24px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 1px 5px 0px #00000040",
        }}
      >
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />
        <DynamicTable
          headers={headers}
          data={tableData}
          loading={isLoading}
          onActionClick={handleActionClick}
          actions={customActions}
        />
      </Box>
      <Box sx={{ height: "20vh" }}></Box>
      <Footer />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Schedule"
        message={
          <>
            Are you sure you want to delete{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {scheduleToDelete?.name || "this schedule"}
            </span>
            ? This action cannot be undone.
          </>
        }
      />
    </>
  );
}

export default RouteSchedule;
